import { memo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";

import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import {
  FundingCardValidationError,
  preValidateFundingCard,
} from "../utils/pre-validate-funding-card";
import { rememberFreshCvv } from "../utils/fresh-cvv-alias";
import { registerFundingCard } from "../utils/register-funding-card";
import { resolveAddCardFundingClass } from "../utils/resolve-add-card-funding-class";
import { paymentType } from "../interfaces/payment";
import { isDeadLinkError } from "../services/api";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import type { AddCardFullState } from "./BillingAddressPage";
import { getEvents, safeCapture } from "../analytics";

let isPreValidateInFlight = false;

const MIN_PROCESSING_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_PROCESSING_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * Add-card step 3: VGS tokenize + CVS pre-validate + register (no rails).
 * Rail provision stays lazy on RecipientPage confirm (needs capabilityKey).
 */
export const AddCardProcessingPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme, setCardList, setPaymentState, setFundingInstrument } =
    useStore();
  const { session } = useSessionStore();
  const pendingFundingMethod = useRequestStore((s) => s.pendingFundingMethod);
  const setPendingFundingMethod = useRequestStore(
    (s) => s.setPendingFundingMethod
  );
  const pendingSourceCountry = useRequestStore((s) => s.pendingSourceCountry);
  const pendingDestinationCountry = useRequestStore(
    (s) => s.pendingDestinationCountry
  );
  const composeDraft = useRequestStore((s) => s.composeDraft);
  const draft = useRequestStore((s) => s.draft);
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  const formData = location.state as AddCardFullState | undefined;
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().ADD_CARD_PROCESSING_VIEW);
  }, []);

  useEffect(() => {
    if (!formData?.card || !formData?.address) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
      return;
    }

    if (isPreValidateInFlight) return;
    isPreValidateInFlight = true;

    const { card, address } = formData;

    const runPreValidate = async () => {
      try {
        const linkId = session.data?.linkId;
        if (!linkId) throw new Error("No linkId found in session");

        const sourceCountry =
          pendingSourceCountry ||
          composeDraft?.sourceCountry ||
          draft?.sourceCountry ||
          session.data?.config?.defaultCorridor?.originCountryCode ||
          session.data?.config?.corridors?.[0]?.originCountryCode ||
          "US";
        const destCountry =
          pendingDestinationCountry ||
          composeDraft?.destinationCountry ||
          draft?.destinationCountry ||
          session.data?.config?.defaultCorridor?.destinationCountryCode ||
          session.data?.config?.corridors?.[0]?.destinationCountryCode ||
          "MX";

        const cleanedNumber = card.cardNumber.replace(/\s/g, "");

        const billingAddress = {
          street1: address.street1.trim(),
          street2: address.street2.trim() || undefined,
          city: address.city.trim(),
          state: address.state,
          zipCode: address.zipCode.trim(),
          country: address.country || "US",
        };

        const pre = await preValidateFundingCard(linkId, {
          cardholderName: card.cardHolderName,
          pan: cleanedNumber,
          expiry: card.expiry,
          cvv: card.cvv,
          billingAddress: {
            street1: billingAddress.street1,
            street2: billingAddress.street2,
            city: billingAddress.city,
            state: billingAddress.state,
            zipCode: billingAddress.zipCode,
            country: billingAddress.country,
          },
          sourceCountry,
          destinationCountry: destCountry,
        });

        const saved = await registerFundingCard(linkId, {
          source: "new",
          cardholderName: card.cardHolderName.trim(),
          pan: cleanedNumber,
          expiry: card.expiry,
          billingAddress,
          cvsValidationId: pre.validationId,
          cvvToken: pre.cvvToken,
          numberToken: pre.numberToken,
          brand: pre.brand,
          last4: pre.last4,
        });

        rememberFreshCvv(saved.paymentMethodId, pre.cvvToken);

        const displayName = `${(saved.brand || "").toUpperCase()}-${saved.last4}`;
        const outcome = resolveAddCardFundingClass({
          intendedMethod: pendingFundingMethod,
          fundingMethod: saved.fundingMethod ?? pre.fundingMethod,
        });
        const noticeCard = {
          paymentMethodId: saved.paymentMethodId,
          brand: saved.brand,
          last4: saved.last4,
          productClass: pre.productClass,
          fundingMethod:
            outcome.kind === "classified" ? outcome.fundingMethod : undefined,
        };

        setCardList([
          ...useStore.getState().cardList,
          {
            cardId: saved.paymentMethodId,
            name: displayName,
            brand: saved.brand,
            last4: saved.last4,
            accountNumber: null,
            expirationDate: null,
            fullName: null,
            cvv: null,
          },
        ]);

        if (outcome.kind === "classified") {
          setPendingFundingMethod(outcome.fundingMethod);
          setFundingInstrument({
            ...saved,
            // One-shot alias for silent first rail provision on Transfer.
            pendingCvvToken: pre.cvvToken,
          });
          setPaymentState({
            payment: {
              cardId: saved.paymentMethodId,
              type: paymentType.CARD,
              brand: displayName,
            },
          });
        } else {
          setPendingFundingMethod(null);
          setFundingInstrument(null);
          setPaymentState({ payment: null });
        }

        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          card_brand: saved.brand,
          funding_payment_method_id: saved.paymentMethodId,
          last4: saved.last4,
        });

        await holdMinimumVisible(visibleSinceRef.current);
        redirectTo(APP_ROUTES.TRANSFER_PAGE, {
          replace: true,
          state:
            outcome.kind === "unclassified"
              ? { cardClassNotice: { kind: "unclassified", card: noticeCard } }
              : outcome.changed
                ? {
                    cardClassNotice: {
                      kind: "changed",
                      fundingMethod: outcome.fundingMethod,
                      card: noticeCard,
                    },
                  }
                : undefined,
        });
      } catch (error) {
        console.error("Failed to register card:", error);

        // Link expired / revoked: the dead-link handler already routed to the
        // screen that explains why the link stopped working.
        if (isDeadLinkError(error)) {
          return;
        }

        const isRateLimited =
          axios.isAxiosError(error) && error.response?.status === 429;

        const axiosCode = axios.isAxiosError(error)
          ? (error.response?.data?.error?.code as string | undefined)
          : undefined;
        const validationCode =
          error instanceof FundingCardValidationError
            ? error.code
            : axiosCode === "CARD_UNDER_REVIEW" ||
                axiosCode === "CARD_VALIDATION_FAILED" ||
                axiosCode === "CARD_PULL_NOT_SUPPORTED" ||
                axiosCode === "CARD_CORRIDOR_CONTEXT_REQUIRED" ||
                axiosCode === "PAYMENT_METHOD_ALREADY_REGISTERED"
              ? axiosCode
              : undefined;

        safeCapture(getEvents().CARD_REGISTERED, {
          success: false,
          error_code: validationCode ?? "REGISTER_FAILED",
        });

        const failureMessage =
          validationCode === "CARD_UNDER_REVIEW"
            ? t("card.underReview")
            : validationCode === "CARD_VALIDATION_FAILED"
              ? t("card.validationFailed")
              : validationCode === "CARD_PULL_NOT_SUPPORTED"
                ? t("card.pullNotSupported")
                : validationCode === "CARD_CORRIDOR_CONTEXT_REQUIRED"
                  ? t("card.corridorContextRequired")
                  : validationCode === "PAYMENT_METHOD_ALREADY_REGISTERED"
                    ? t("card.alreadyRegistered")
                    : validationCode === "VGS_CONFIG_UNAVAILABLE"
                      ? t("vgs.configUnavailable")
                      : t("card.registrationFailed");

        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: isRateLimited
            ? {
                title: t("error.rateLimitTitle"),
                subTitle: t("error.rateLimitSubtitle"),
                message: t("error.rateLimitMessage"),
                btnText: t("card.tryAgain"),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              }
            : {
                title: t("card.registrationFailedTitle"),
                subTitle: t("card.registrationFailedSubtitle"),
                message: failureMessage,
                btnText: t("card.tryAgain"),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              },
        });
      } finally {
        isPreValidateInFlight = false;
      }
    };

    runPreValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
